import { MAX_PARTICIPANTS_PER_COMPETITION } from "@mebi/shared";
import { z } from "zod";

const JsonString = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((val) => {
    if (typeof val === "string") {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  }, schema);

export const PrizeSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  amount: z.string().optional(),
  quantity: z.number().optional(),
  description: z.string().optional(),
  type: z.string().optional(),
});

const BaseGameTypeDetailSchema = z.object({
  name: z.string().trim().min(1, "Tên nội dung là bắt buộc"),
  formatCode: z.string().min(1, "Hình thức thi đấu là bắt buộc"),
  compositionCode: z.string().min(1, "Thành phần thi đấu là bắt buộc"),
  ranks: z.array(z.string()).optional(),
  maxParticipants: z
    .string()
    .min(1, "Số lượng người tham gia là bắt buộc")
    .regex(/^\d+$/, "Số lượng người tham gia phải là số")
    .refine((value) => Number.parseInt(value, 10) > 0, {
      message: "Số lượng người tham gia phải lớn hơn 0",
    })
    .refine((value) => Number.parseInt(value, 10) <= MAX_PARTICIPANTS_PER_COMPETITION, {
      message: `Mỗi nội dung chỉ được tối đa ${MAX_PARTICIPANTS_PER_COMPETITION} người tham gia`,
    }),
  province: z.string().min(1, "Tỉnh/Thành phố là bắt buộc"),
  ward: z.string().min(1, "Phường/Xã là bắt buộc"),
  detailAddress: z.string().min(1, "Địa chỉ chi tiết là bắt buộc"),
  description: z.string().optional(),
  thirdPlaceMatch: z.boolean().optional(),
  applyRanking: z.boolean().default(true),
  useMebiRanking: z.boolean().optional(),
  hasFee: z.boolean().optional(),
  registrationFee: z.string().optional(),
  accountNumber: z.string().optional(),
  accountName: z.string().optional(),
  bankName: z.string().optional(),
  eventStartDate: z.string().min(1, "Ngày bắt đầu thi đấu là bắt buộc"),
  eventEndDate: z.string().min(1, "Ngày kết thúc thi đấu là bắt buộc"),
  registrationStartDate: z.string().min(1, "Ngày bắt đầu đăng ký là bắt buộc"),
  registrationEndDate: z.string().min(1, "Ngày kết thúc đăng ký là bắt buộc"),
  billiardsTypeId: z.string().min(1, "Thể loại là bắt buộc"),
  billiardsSubtypeId: z.string().min(1, "Thể loại chi tiết là bắt buộc"),
  prizes: z.array(PrizeSchema).optional(),
  regulationUrl: z.string().optional(),
  qrCodeUrl: z.string().optional(),
});

const validatePayment = (
  data: z.infer<typeof BaseGameTypeDetailSchema>,
  ctx: z.RefinementCtx,
) => {
  if (data.hasFee) {
    if (!data.registrationFee?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Lệ phí đăng ký là bắt buộc khi có phí",
        path: ["registrationFee"],
      });
    }
    if (!data.accountNumber?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Số tài khoản là bắt buộc khi có lệ phí",
        path: ["accountNumber"],
      });
    }
    if (!data.accountName?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Tên tài khoản là bắt buộc khi có lệ phí",
        path: ["accountName"],
      });
    }
    if (!data.bankName?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Tên ngân hàng là bắt buộc khi có lệ phí",
        path: ["bankName"],
      });
    }
  }
};

const validateGameTypeDates = (
  data: z.infer<typeof BaseGameTypeDetailSchema>,
  ctx: z.RefinementCtx,
) => {
  // Date validation
  if (data.registrationStartDate && data.registrationEndDate) {
    const regStart = new Date(data.registrationStartDate);
    const regEnd = new Date(data.registrationEndDate);
    if (regEnd < regStart) {
      ctx.addIssue({
        code: "custom",
        message: "Ngày kết thúc đăng ký phải sau ngày bắt đầu",
        path: ["registrationEndDate"],
      });
    }
  }

  if (data.eventStartDate && data.eventEndDate) {
    const eventStart = new Date(data.eventStartDate);
    const eventEnd = new Date(data.eventEndDate);
    if (eventEnd < eventStart) {
      ctx.addIssue({
        code: "custom",
        message: "Ngày kết thúc thi đấu phải sau ngày bắt đầu",
        path: ["eventEndDate"],
      });
    }
  }
};

export const GameTypeDetailSchema = BaseGameTypeDetailSchema.superRefine(
  (data, ctx) => {
    validatePayment(data, ctx);
    validateGameTypeDates(data, ctx);
  },
);

export const CreateTournamentBaseSchema = z.object({
  name: z.string().min(1, "Tên giải đấu là bắt buộc"),
  startDate: z.string().min(1, "Ngày bắt đầu là bắt buộc"),
  endDate: z.string().min(1, "Ngày kết thúc là bắt buộc"),
  province: z.string().min(1, "Tỉnh/Thành phố là bắt buộc"),
  ward: z.string().min(1, "Quận/Huyện là bắt buộc"),
  detailAddress: z.string().min(1, "Địa chỉ chi tiết là bắt buộc"),
  description: z.string().optional(),
  banner: z.string().optional(),
  gameTypes: JsonString(
    z
      .array(z.string())
      .min(1, "Phải có ít nhất một nội dung thi đấu")
      .max(20, "Mỗi giải chỉ được tối đa 20 nội dung thi đấu"),
  ),
  registrationStartDate: z.string().optional(),
  registrationEndDate: z.string().optional(),
  gameTypeDetails: JsonString(
    z.record(z.string(), GameTypeDetailSchema).optional(),
  ),
  prizes: JsonString(z.array(PrizeSchema).optional()),
  regulationUrl: z.string().optional(),
  qrCodeUrl: z.string().optional(),
  billiardsTypeId: z.string().optional(),
  billiardsSubtypeId: z.string().optional(),
});

export const CreateTournamentSchema = CreateTournamentBaseSchema.superRefine(
  (data, ctx) => {
    // Date validation
    if (data.startDate && data.endDate) {
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        ctx.addIssue({
          code: "custom",
          message: "Ngày bắt đầu không thể là ngày trong quá khứ",
          path: ["startDate"],
        });
      }

      if (endDate < startDate) {
        ctx.addIssue({
          code: "custom",
          message: "Ngày kết thúc phải sau ngày bắt đầu",
          path: ["endDate"],
        });
      }
    }

    // Registration period validation
    if (data.registrationStartDate && data.registrationEndDate) {
      const regStart = new Date(data.registrationStartDate);
      const regEnd = new Date(data.registrationEndDate);
      if (regEnd < regStart) {
        ctx.addIssue({
          code: "custom",
          message: "Ngày kết thúc đăng ký phải sau ngày bắt đầu",
          path: ["registrationEndDate"],
        });
      }
      if (data.startDate) {
        const eventStart = new Date(data.startDate);
        if (regEnd > eventStart) {
          ctx.addIssue({
            code: "custom",
            message: "Ngày kết thúc đăng ký không được sau ngày bắt đầu giải",
            path: ["registrationEndDate"],
          });
        }
      }
    }
  },
);

export type CreateTournamentInput = z.infer<typeof CreateTournamentSchema>;
export type GameTypeDetailInput = z.infer<typeof GameTypeDetailSchema>;
export type PrizeInput = z.infer<typeof PrizeSchema>;
